/**
 * Bible state core — navigation, display, UI, chat, user data delegation.
 *
 * Base class for BibleState. Extracted to keep files under ~150 lines.
 */

import type { BibleLocation, Bookmark, Note, PrayerItem, ConversationSummary, StudyAiCommentary, VariantAnalysis } from '@skjv/common';
import { MemoryVerseFrequency, formatReference } from '@skjv/common';
import { loadLocationFromHash } from './persistence';
import { DisplayState, StrongsState, VerseAnalysisState, type ThemeMode, type ColorTheme, type StrongsDisplay, type VerseAnalysisDisplay, type VerseVerdictState } from './display';
import { NavigationState } from './navigation';
import { UiState, type UserDataTab, type ConfirmDialogConfig, type ToastConfig, type PericopeStudyPrompt } from './ui';
import { BookmarksState, NotesState, PrayerState, MemoryVerseState } from './user-data';
import { ChatState, type ChatMessage, type CommentaryHistoryEntry } from './chat';
import { SearchState } from './search';

export class BibleStateCore {
  protected readonly displayStore = new DisplayState();
  protected readonly strongsStore = new StrongsState();
  protected readonly verseAnalysisStore = new VerseAnalysisState();
  protected readonly navigationStore = new NavigationState();
  readonly ui = new UiState();
  protected readonly bookmarksStore = new BookmarksState();
  protected readonly notesStore = new NotesState();
  protected readonly prayerStore = new PrayerState();
  protected readonly memoryStore = new MemoryVerseState();
  protected readonly chatStore = new ChatState();
  protected readonly searchStore = new SearchState();

  // ── Navigation ──────────────────────────────────────────────

  get currentLocation(): BibleLocation { return this.navigationStore.currentLocation; }
  get bibleHistory(): BibleLocation[] { return this.navigationStore.bibleHistory; }
  get isScrollUpdate(): boolean { return this.navigationStore.isScrollUpdate; }
  get isLoading(): boolean { return this.navigationStore.isLoading; }
  get currentReference(): string { return this.navigationStore.currentReference; }
  get currentBookName(): string { return this.navigationStore.currentBookName; }
  get currentChapterNumber(): number { return this.navigationStore.currentChapterNumber; }

  setCurrentLocation(location: BibleLocation): void { this.navigationStore.setCurrentLocation(location); }
  addLocationToHistory(location: BibleLocation): void { this.navigationStore.addLocationToHistory(location); }
  setLocationFromScroll(location: BibleLocation): void { this.navigationStore.setLocationFromScroll(location); }
  updateDisplayLocation(location: BibleLocation): void { this.navigationStore.updateDisplayLocation(location); }
  loadLocationFromHash(): BibleLocation | null { return loadLocationFromHash(); }

  // ── Display ─────────────────────────────────────────────────

  get themeMode(): ThemeMode { return this.displayStore.themeMode; }
  get isDarkMode(): boolean { return this.displayStore.isDarkMode; }
  get fontScale(): number { return this.displayStore.fontScale; }
  get colorTheme(): ColorTheme { return this.displayStore.colorTheme; }
  get showPericopeHeadings(): boolean { return this.displayStore.showPericopeHeadings; }
  get isReadingMode(): boolean { return this.displayStore.isReadingMode; }

  setThemeMode(mode: ThemeMode): void { this.displayStore.setThemeMode(mode); }
  setFontScale(scale: number): void { this.displayStore.setFontScale(scale); }
  setColorTheme(theme: ColorTheme): void { this.displayStore.setColorTheme(theme); }
  setShowPericopeHeadings(show: boolean): void { this.displayStore.setShowPericopeHeadings(show); }
  setReadingMode(on: boolean): void { this.displayStore.setReadingMode(on); }

  // ── Strong's ────────────────────────────────────────────────

  get strongsDisplay(): StrongsDisplay | null { return this.strongsStore.strongsDisplay; }
  showStrongsDefinition(numbers: string, location: BibleLocation, opts?: { englishWord?: string; websterDefinition?: string }): void { this.strongsStore.showStrongsDefinition(numbers, location, opts); }
  hideStrongsDefinition(): void { this.strongsStore.hideStrongsDefinition(); }

  // ── Verse Analysis ──────────────────────────────────────────

  get verseAnalysisDisplay(): VerseAnalysisDisplay | null { return this.verseAnalysisStore.analysisDisplay; }
  verseAnalysisVerdictFor(location: BibleLocation): VerseVerdictState | undefined { return this.verseAnalysisStore.verdictFor(location); }
  loadVerseAnalysisVerdict(location: BibleLocation): void { this.verseAnalysisStore.loadVerdict(location); }
  showVerseAnalysis(analysis: VariantAnalysis, location: BibleLocation): void { this.verseAnalysisStore.showVerseAnalysis(analysis, location); }
  openVerseAnalysisAt(location: BibleLocation): void { this.verseAnalysisStore.openAnalysisAt(location); }
  hideVerseAnalysis(): void { this.verseAnalysisStore.hideVerseAnalysis(); }

  // ── UI Panels ───────────────────────────────────────────────

  get interactionLocation(): BibleLocation | null { return this.ui.interactionLocation; }
  get verseMenuVisible(): boolean { return this.ui.verseMenuVisible; }
  get noteEditorVisible(): boolean { return this.ui.noteEditorVisible; }
  get bookmarkCreatorVisible(): boolean { return this.ui.bookmarkCreatorVisible; }
  get userDataPanelVisible(): boolean { return this.ui.userDataPanelVisible; }
  get userDataPanelTab(): UserDataTab { return this.ui.userDataPanelTab; }

  setInteractionLocation(location: BibleLocation | null): void { this.ui.setInteractionLocation(location); }
  showVerseMenu(): void { this.ui.showVerseMenu(); }
  hideVerseMenu(): void { this.ui.hideVerseMenu(); }
  showNoteEditor(): void { this.ui.showNoteEditor(); }
  hideNoteEditor(): void { this.ui.hideNoteEditor(); }
  showUserDataPanel(tab: UserDataTab): void { this.ui.showUserDataPanel(tab); }
  hideUserDataPanel(): void { this.ui.hideUserDataPanel(); }
  setUserDataPanelTab(tab: UserDataTab): void { this.ui.setUserDataPanelTab(tab); }
  showBookmarkCreator(): void { this.ui.showBookmarkCreator(); }
  hideBookmarkCreator(): void { this.ui.hideBookmarkCreator(); }

  // ── Chat ────────────────────────────────────────────────────

  get chatPanelVisible(): boolean { return this.ui.chatPanelVisible; }
  get chatFullscreen(): boolean { return this.ui.chatFullscreen; }
  get chatMessages(): ChatMessage[] { return this.chatStore.messages; }
  get isChatLoading(): boolean { return this.chatStore.isLoading; }
  get chatConversations(): ConversationSummary[] { return this.chatStore.conversations; }
  get activeConversationId(): string | null { return this.chatStore.activeConversationId; }
  get isConversationListVisible(): boolean { return this.chatStore.isConversationListVisible; }
  get isConversationsLoaded(): boolean { return this.chatStore.isConversationsLoaded; }

  get isChatComposerVisible(): boolean { return this.chatStore.isComposerVisible; }

  showChatPanel(): void {
    this.ui.showChatPanel();
    this.chatStore.showComposerIfNewChat();
  }
  hideChatPanel(): void { this.ui.hideChatPanel(); }
  toggleChatFullscreen(): void { this.ui.toggleChatFullscreen(); }
  sendChatMessage(content: string): void { this.chatStore.sendMessage(content); }
  markChatTheaterDone(messageId: string): void { this.chatStore.markTheaterDone(messageId); }
  addVerseStudy(reference: string): void { this.chatStore.addVerseStudy(reference); }
  clearChatHistory(): void { this.chatStore.clearHistory(); }
  loadChatConversations(): void { this.chatStore.loadConversations(); }
  selectChatConversation(id: string): void { this.chatStore.selectConversation(id); }
  startNewChatConversation(): void { this.chatStore.startNewConversation(); }
  deleteChatConversation(id: string): void { this.chatStore.deleteConversation(id); }
  toggleConversationList(): void { this.chatStore.toggleConversationList(); }
  hideConversationList(): void { this.chatStore.hideConversationList(); }
  get isCommentaryHistoryVisible(): boolean { return this.chatStore.isCommentaryHistoryVisible; }
  toggleCommentaryHistory(): void { this.chatStore.toggleCommentaryHistory(); }
  hideCommentaryHistory(): void { this.chatStore.hideCommentaryHistory(); }

  /** Open the AI viewer showing a study's attached commentary. */
  showStudyCommentary(commentary: StudyAiCommentary, readerThoughts?: string): void {
    this.ui.showChatPanel();
    this.chatStore.showStoredCommentary({
      pericopeTitle: commentary.pericopeTitle,
      reference: commentary.reference,
      text: commentary.text,
      generatedAt: commentary.generatedAt,
      readerThoughts,
    });
  }

  /** Open the AI viewer showing a commentary picked from the history browser. */
  showCommentaryFromHistory(entry: CommentaryHistoryEntry): void {
    this.chatStore.hideCommentaryHistory();
    this.ui.showChatPanel();
    this.chatStore.showStoredCommentary(entry);
  }
  pingPipeline(): void { this.chatStore.pingPipeline(); }
  requestChatCommentary(messageId: string, readerThoughts?: string): void {
    this.chatStore.requestCommentary(messageId, readerThoughts);
  }

  /** Re-run a commentary that failed, from its error bubble. */
  retryChatCommentary(failedMessageId: string): void {
    this.chatStore.retryCommentary(failedMessageId);
  }

  // ── Pericope study ──────────────────────────────────────────

  get pericopeStudyPrompt(): PericopeStudyPrompt | null { return this.ui.pericopeStudyPrompt; }
  showPericopeStudyPrompt(location: BibleLocation, title: string, anchorRect: DOMRect): void {
    this.ui.showPericopeStudyPrompt(location, title, anchorRect);
  }
  hidePericopeStudyPrompt(): void { this.ui.hidePericopeStudyPrompt(); }

  /** Open the AI drawer and run a whole-pericope study for a location. */
  startPericopeStudyAt(location: BibleLocation): void {
    this.ui.hidePericopeStudyPrompt();
    this.ui.showChatPanel();
    this.chatStore.addPericopeStudy(formatReference(location));
  }

  /** Popover Go — run the pericope study for the prompted heading. */
  startPericopeStudy(): void {
    const prompt = this.ui.pericopeStudyPrompt;
    if (prompt) this.startPericopeStudyAt(prompt.location);
  }

  // ── Search ─────────────────────────────────────────────────

  get searchPanelVisible(): boolean { return this.ui.userDataPanelVisible && this.ui.userDataPanelTab === 'search'; }
  get searchResults() { return this.searchStore.results; }
  get searchTotal(): number { return this.searchStore.total; }
  get searchText(): string { return this.searchStore.searchText; }
  get searchHighlightTerms(): string { return this.searchStore.highlightTerms; }
  get isSearchExact(): boolean { return this.searchStore.isExact; }
  get isSearching(): boolean { return this.searchStore.isSearching; }
  get searchHasMore(): boolean { return this.searchStore.hasMore; }
  get searchReferenceMatch(): BibleLocation | null { return this.searchStore.referenceMatch; }
  get searchReferenceLabel(): string { return this.searchStore.referenceLabel; }

  showSearchPanel(): void { this.ui.showUserDataPanel('search'); }
  hideSearchPanel(): void { this.ui.hideUserDataPanel(); }
  toggleSearchPanel(): void {
    if (this.searchPanelVisible) {
      this.ui.hideUserDataPanel();
    } else {
      this.ui.showUserDataPanel('search');
    }
  }
  setSearchText(text: string): void { this.searchStore.setSearchText(text); }
  setSearchExact(isExact: boolean): void { this.searchStore.setIsExact(isExact); }
  loadMoreSearchResults(): void { this.searchStore.loadMore(); }
  clearSearch(): void { this.searchStore.clear(); }
  jumpToSearchReference(): boolean {
    const loc = this.searchStore.referenceMatch;
    if (!loc) return false;
    this.navigationStore.setCurrentLocation(loc);
    this.searchStore.clear();
    return true;
  }

  // ── Confirm Dialog + Toasts ─────────────────────────────────

  get confirmDialog(): ConfirmDialogConfig | null { return this.ui.confirmDialog; }
  showConfirmDialog(config: Omit<ConfirmDialogConfig, 'id'>): void { this.ui.showConfirmDialog(config); }
  hideConfirmDialog(): void { this.ui.hideConfirmDialog(); }

  get toasts(): ToastConfig[] { return this.ui.toasts; }
  showToast(message: string, variant?: ToastConfig['variant'], duration?: number): void { this.ui.showToast(message, variant, duration); }
  removeToast(id: string): void { this.ui.removeToast(id); }

  // ── Bookmarks ───────────────────────────────────────────────

  get bookmarks(): Bookmark[] { return this.bookmarksStore.bookmarks; }
  get sortedBookmarks(): Bookmark[] { return this.bookmarksStore.sortedBookmarks; }
  get bookmarksAtInteractionLocation(): Bookmark[] { return this.bookmarksStore.getBookmarksAt(this.ui.interactionLocation); }
  getBookmarksAt(location: BibleLocation): Bookmark[] { return this.bookmarksStore.getBookmarksAt(location); }

  addBookmark(name: string, isFavorite: boolean = false): void {
    if (this.ui.interactionLocation) this.bookmarksStore.addBookmark(this.ui.interactionLocation, name, isFavorite);
  }

  removeBookmark(name: string): void { this.bookmarksStore.removeBookmark(name); }
  editBookmark(oldName: string, newName: string, isFavorite: boolean): void { this.bookmarksStore.editBookmark(oldName, newName, isFavorite); }
  setFavoriteBookmark(name: string, isFavorite: boolean): void { this.bookmarksStore.setFavoriteBookmark(name, isFavorite); }
  moveBookmark(name: string, newLocation: BibleLocation): void { this.bookmarksStore.moveBookmark(name, newLocation); }

  // ── Notes ───────────────────────────────────────────────────

  get notes(): Note[] { return this.notesStore.notes; }
  get noteAtInteractionLocation(): Note | undefined { return this.notesStore.getNoteAt(this.ui.interactionLocation); }

  saveNote(noteText: string): void {
    if (this.ui.interactionLocation) this.notesStore.saveNote(this.ui.interactionLocation, noteText);
  }

  removeNote(): void {
    if (this.ui.interactionLocation) this.notesStore.removeNote(this.ui.interactionLocation);
  }

  removeNoteAt(location: BibleLocation): void { this.notesStore.removeNote(location); }

  // ── Prayer + Memory Verse ───────────────────────────────────

  get prayerItems(): PrayerItem[] { return this.prayerStore.prayerItems; }
  addPrayerItem(description: string): void { this.prayerStore.addPrayerItem(description); }
  removePrayerItem(id: string): void { this.prayerStore.removePrayerItem(id); }
  reorderPrayerItems(items: PrayerItem[]): void { this.prayerStore.reorderPrayerItems(items); }

  get memoryVerse(): BibleLocation | null { return this.memoryStore.memoryVerse; }
  get memoryVerseFrequency(): MemoryVerseFrequency { return this.memoryStore.memoryVerseFrequency; }
  get memoryVerseLastMillis(): number | null { return this.memoryStore.memoryVerseLastMillis; }
  setMemoryVerse(location: BibleLocation): void { this.memoryStore.setMemoryVerse(location); }
  removeMemoryVerse(): void { this.memoryStore.removeMemoryVerse(); }
  setMemoryVerseFrequency(frequency: MemoryVerseFrequency): void { this.memoryStore.setMemoryVerseFrequency(frequency); }
}
